/**
 * One-shot viewport zoom-out when an onboarding session becomes active
 * on the canvas — either the initial seed or a mid-flow reload that
 * restores the widget.
 *
 * Frames the canvas so the PDF + image column + deck all fit without
 * panning. Applied once per mount — subsequent user pan/zoom is never
 * clobbered.
 */

import { useReactFlow } from "@xyflow/react";
import { useEffect, useRef } from "react";
import { useCanvasOnboardingState } from "../context/CanvasOnboardingContext";

// 1920×1080 is the reference viewport where "everything fits" at 0.75
// zoom. Smaller viewports zoom out proportionally (floored at 0.45 so
// chrome doesn't dwarf the canvas); larger ones keep the 0.75 baseline
// — zooming further in adds nothing.
const REF_W = 1920;
const REF_H = 1080;
const BASE_ZOOM = 0.75;
const MIN_ZOOM = 0.45;

// Delay so the viewport write lands after React Flow has mounted and
// measured itself.
const APPLY_DELAY_MS = 150;

export function useOnboardingCanvasZoom(): void {
  const reactFlowInstance = useReactFlow();
  const { activeOnboarding } = useCanvasOnboardingState();
  const hasActiveOnboarding = !!activeOnboarding;

  const appliedRef = useRef(false);
  useEffect(() => {
    if (appliedRef.current) return;
    if (!hasActiveOnboarding) return;
    appliedRef.current = true;

    const w = window.innerWidth;
    const h = window.innerHeight;
    const scale = Math.min(1, w / REF_W, h / REF_H);
    const zoom = Math.max(MIN_ZOOM, BASE_ZOOM * scale);

    const t = window.setTimeout(() => {
      reactFlowInstance.setViewport({
        x: Math.round(w * 0.25),
        y: Math.round(h * 0.5),
        zoom,
      });
    }, APPLY_DELAY_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveOnboarding]);
}
